import React, { useState } from "react";
import {
  CreditCard,
  Settings,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  Plus,
  Minus,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";
import "./CafeteriaControls.css";

const CafeteriaControls = () => {
  const { user } = useAuth();
  const [selectedChild] = useState("Emma Wilson");
  const [activeTab, setActiveTab] = useState("balance");

  // Mock data for child's cafeteria account
  const childAccount = {
    name: "Emma Wilson",
    studentId: "STU001",
    currentBalance: 45.5,
    dailyLimit: 25.0,
    weeklyLimit: 100.0,
    monthlyLimit: 300.0,
    isActive: true,
    restrictions: ["No sugary drinks", "Vegetarian meals only"],
  };

  const recentTransactions = [
    {
      id: 1,
      item: "Chicken Burger",
      amount: 8.5,
      date: "2024-01-15",
      time: "12:30 PM",
      type: "purchase",
    },
    {
      id: 2,
      item: "Fresh Juice",
      amount: 3.0,
      date: "2024-01-15",
      time: "12:35 PM",
      type: "purchase",
    },
    {
      id: 3,
      item: "Account Top-up",
      amount: 50.0,
      date: "2024-01-14",
      time: "9:00 AM",
      type: "topup",
    },
    {
      id: 4,
      item: "Caesar Salad",
      amount: 6.0,
      date: "2024-01-14",
      time: "12:15 PM",
      type: "purchase",
    },
  ];

  const spendingLimits = [
    {
      period: "Daily",
      current: 11.5,
      limit: 25.0,
      percentage: (11.5 / 25.0) * 100,
    },
    {
      period: "Weekly",
      current: 67.5,
      limit: 100.0,
      percentage: (67.5 / 100.0) * 100,
    },
    {
      period: "Monthly",
      current: 254.5,
      limit: 300.0,
      percentage: (254.5 / 300.0) * 100,
    },
  ];

  const [topupAmount, setTopupAmount] = useState(25);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [newDailyLimit, setNewDailyLimit] = useState(childAccount.dailyLimit);
  const [newWeeklyLimit, setNewWeeklyLimit] = useState(
    childAccount.weeklyLimit
  );
  const [newMonthlyLimit, setNewMonthlyLimit] = useState(
    childAccount.monthlyLimit
  );
  const [newRestrictions, setNewRestrictions] = useState(
    childAccount.restrictions.join(", ")
  );

  const tabs = [
    { id: "balance", label: "Balance & Top-up", icon: DollarSign },
    { id: "limits", label: "Spending Limits", icon: Settings },
    { id: "transactions", label: "Transactions", icon: CreditCard },
    { id: "restrictions", label: "Food Restrictions", icon: AlertTriangle },
  ];

  const handleTopup = () => {
    if (topupAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    toast.success(`$${topupAmount} added to ${selectedChild}'s account`);
    setShowTopupModal(false);
    setTopupAmount(25);
  };

  const handleUpdateLimits = () => {
    toast.success("Spending limits updated successfully");
  };

  const handleUpdateRestrictions = () => {
    const restrictions = newRestrictions
      .split(",")
      .map((r) => r.trim())
      .filter((r) => r);
    toast.success("Food restrictions updated successfully");
  };

  const getLimitColor = (percentage) => {
    if (percentage >= 90) return "danger";
    if (percentage >= 75) return "warning";
    return "success";
  };

  const renderBalance = () => (
    <div className="balance-content">
      <div className="balance-overview">
        <div className="balance-card">
          <div className="balance-header">
            <h3>Current Balance</h3>
            <span className="balance-amount">
              ${childAccount.currentBalance.toFixed(2)}
            </span>
          </div>
          <div className="balance-status">
            <span
              className={`status-indicator ${
                childAccount.isActive ? "active" : "inactive"
              }`}
            ></span>
            <span>
              {childAccount.isActive ? "Account Active" : "Account Suspended"}
            </span>
          </div>
        </div>

        <div className="quick-actions">
          <h4>Quick Actions</h4>
          <div className="action-buttons">
            <button
              className="action-btn primary"
              onClick={() => setShowTopupModal(true)}
              aria-label="Add funds to child's cafeteria account"
            >
              <Plus size={16} />
              Add Funds
            </button>
            <button
              className="action-btn secondary"
              onClick={() => toast("Account suspended", { icon: "ℹ️" })}
              aria-label="Suspend child's cafeteria account"
            >
              <Minus size={16} />
              Suspend Account
            </button>
          </div>
        </div>
      </div>

      <div className="spending-summary">
        <h4>Spending Summary</h4>
        <div className="spending-cards">
          {spendingLimits.map((limit) => (
            <div key={limit.period} className="spending-card">
              <div className="spending-header">
                <span className="period">{limit.period}</span>
                <span className={`amount ${getLimitColor(limit.percentage)}`}>
                  ${limit.current.toFixed(2)} / ${limit.limit.toFixed(2)}
                </span>
              </div>
              <div className="progress-bar">
                <div
                  className={`progress-fill ${getLimitColor(limit.percentage)}`}
                  style={{ width: `${Math.min(limit.percentage, 100)}%` }}
                ></div>
              </div>
              <div className="progress-text">
                {limit.percentage.toFixed(1)}% used
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderLimits = () => (
    <div className="limits-content">
      <div className="limits-form">
        <h4>Set Spending Limits</h4>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="daily-limit">Daily Limit ($)</label>
            <input
              type="number"
              id="daily-limit"
              value={newDailyLimit}
              onChange={(e) => setNewDailyLimit(parseFloat(e.target.value))}
              min="0"
              step="0.01"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="weekly-limit">Weekly Limit ($)</label>
            <input
              type="number"
              id="weekly-limit"
              value={newWeeklyLimit}
              onChange={(e) => setNewWeeklyLimit(parseFloat(e.target.value))}
              min="0"
              step="0.01"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="monthly-limit">Monthly Limit ($)</label>
            <input
              type="number"
              id="monthly-limit"
              value={newMonthlyLimit}
              onChange={(e) => setNewMonthlyLimit(parseFloat(e.target.value))}
              min="0"
              step="0.01"
              className="form-input"
            />
          </div>
        </div>
        <button className="update-btn" onClick={handleUpdateLimits}>
          <CheckCircle size={16} />
          Update Limits
        </button>
      </div>

      <div className="limits-info">
        <h4>Current Limits</h4>
        <div className="limits-list">
          <div className="limit-item">
            <span className="limit-period">Daily</span>
            <span className="limit-amount">
              ${childAccount.dailyLimit.toFixed(2)}
            </span>
          </div>
          <div className="limit-item">
            <span className="limit-period">Weekly</span>
            <span className="limit-amount">
              ${childAccount.weeklyLimit.toFixed(2)}
            </span>
          </div>
          <div className="limit-item">
            <span className="limit-period">Monthly</span>
            <span className="limit-amount">
              ${childAccount.monthlyLimit.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTransactions = () => (
    <div className="transactions-content">
      <div className="transactions-header">
        <h4>Recent Transactions</h4>
        <span className="transactions-count">
          {recentTransactions.length} transactions
        </span>
      </div>
      <div className="transactions-list">
        {recentTransactions.map((transaction) => (
          <div key={transaction.id} className="transaction-item">
            <div className="transaction-icon">
              {transaction.type === "purchase" ? (
                <CreditCard size={20} className="purchase-icon" />
              ) : (
                <Plus size={20} className="topup-icon" />
              )}
            </div>
            <div className="transaction-details">
              <div className="transaction-title">{transaction.item}</div>
              <div className="transaction-meta">
                {transaction.date} • {transaction.time}
              </div>
            </div>
            <div className={`transaction-amount ${transaction.type}`}>
              {transaction.type === "purchase" ? "-" : "+"}$
              {transaction.amount.toFixed(2)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderRestrictions = () => (
    <div className="restrictions-content">
      <div className="restrictions-form">
        <h4>Food Restrictions & Allergies</h4>
        <div className="form-group">
          <label htmlFor="restrictions">Restrictions (comma-separated)</label>
          <textarea
            id="restrictions"
            value={newRestrictions}
            onChange={(e) => setNewRestrictions(e.target.value)}
            placeholder="e.g., No nuts, Vegetarian, No dairy"
            rows={4}
            className="form-textarea"
          />
        </div>
        <button className="update-btn" onClick={handleUpdateRestrictions}>
          <CheckCircle size={16} />
          Update Restrictions
        </button>
      </div>

      <div className="current-restrictions">
        <h4>Current Restrictions</h4>
        <div className="restrictions-list">
          {childAccount.restrictions.map((restriction, index) => (
            <div key={index} className="restriction-item">
              <AlertTriangle size={16} className="restriction-icon" />
              <span>{restriction}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="cafeteria-controls">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <Settings size={24} />
          </div>
          <div>
            <h1>Cafeteria Controls</h1>
            <p>Manage your child's cafeteria account and spending</p>
          </div>
        </div>
        <div className="child-info">
          <div className="child-avatar">👩‍🎓</div>
          <div className="child-details">
            <h3>{childAccount.name}</h3>
            <p>{childAccount.studentId}</p>
          </div>
        </div>
      </div>

      <div className="controls-content">
        <div className="tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={20} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="tab-content">
          {activeTab === "balance" && renderBalance()}
          {activeTab === "limits" && renderLimits()}
          {activeTab === "transactions" && renderTransactions()}
          {activeTab === "restrictions" && renderRestrictions()}
        </div>
      </div>

      {/* Top-up Modal */}
      {showTopupModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Funds</h3>
              <button
                className="modal-close"
                onClick={() => setShowTopupModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="topup-form">
                <div className="form-group">
                  <label htmlFor="amount">Amount to Add ($)</label>
                  <input
                    type="number"
                    id="amount"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(parseFloat(e.target.value))}
                    min="1"
                    step="0.01"
                    className="form-input"
                  />
                </div>
                <div className="quick-amounts">
                  <button
                    className={`quick-amount-btn ${
                      topupAmount === 10 ? "selected" : ""
                    }`}
                    onClick={() => setTopupAmount(10)}
                  >
                    $10
                  </button>
                  <button
                    className={`quick-amount-btn ${
                      topupAmount === 25 ? "selected" : ""
                    }`}
                    onClick={() => setTopupAmount(25)}
                  >
                    $25
                  </button>
                  <button
                    className={`quick-amount-btn ${
                      topupAmount === 50 ? "selected" : ""
                    }`}
                    onClick={() => setTopupAmount(50)}
                  >
                    $50
                  </button>
                  <button
                    className={`quick-amount-btn ${
                      topupAmount === 100 ? "selected" : ""
                    }`}
                    onClick={() => setTopupAmount(100)}
                  >
                    $100
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowTopupModal(false)}
              >
                Cancel
              </button>
              <button className="btn-primary" onClick={handleTopup}>
                Add Funds
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CafeteriaControls;
